import { useState } from 'react'
import { Search, FileText } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { useChains } from '@/hooks/useChains'
import { getFileContents } from '@/services/api'
import type { ContractFile } from '@/types'

export function ContractLookup() {
  const { chains, loading: chainsLoading } = useChains()
  const [address, setAddress] = useState('')
  const [selectedChain, setSelectedChain] = useState('')
  const [loading, setLoading] = useState(false)
  const [files, setFiles] = useState<ContractFile[]>([])
  const [error, setError] = useState<string | null>(null)

  const handleLookup = async () => {
    if (!address || !selectedChain) {
      return
    }

    setLoading(true)
    setError(null)
    setFiles([])

    try {
      const data = await getFileContents(selectedChain, address)
      if (data.length === 0) {
        setError('No verified contract found at this address')
      } else {
        setFiles(data)
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to lookup contract')
    } finally {
      setLoading(false)
    }
  }

  return (
    <Card className="w-full max-w-2xl mx-auto">
      <CardHeader>
        <CardTitle>Lookup Verified Contract</CardTitle>
        <CardDescription>
          Search for already verified contracts by address and network
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-2">
          <Label htmlFor="lookup-chain">Blockchain Network</Label>
          <select
            id="lookup-chain"
            value={selectedChain}
            onChange={(e) => setSelectedChain(e.target.value)}
            disabled={chainsLoading}
            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <option value="">Select a network...</option>
            {chains.map((chain) => (
              <option key={chain.chainId} value={chain.chainId}>
                {chain.name} ({chain.shortName})
              </option>
            ))}
          </select>
        </div>

        <div className="space-y-2">
          <Label htmlFor="lookup-address">Contract Address</Label>
          <div className="flex gap-2">
            <Input
              id="lookup-address"
              placeholder="0x..."
              value={address}
              onChange={(e) => setAddress(e.target.value)}
            />
            <Button
              onClick={handleLookup}
              disabled={!address || !selectedChain || loading}
            >
              <Search className="h-4 w-4 mr-2" />
              Search
            </Button>
          </div>
        </div>

        {error && (
          <div className="p-4 rounded-lg border bg-muted">
            <p className="text-sm text-muted-foreground">{error}</p>
          </div>
        )}

        {files.length > 0 && (
          <div className="space-y-4">
            <h3 className="text-sm font-medium">Verified Source Files</h3>
            {files.map((file, index) => (
              <div key={index} className="border rounded-lg overflow-hidden">
                <div className="bg-muted px-4 py-2 flex items-center gap-2">
                  <FileText className="h-4 w-4" />
                  <span className="text-sm font-medium">{file.name}</span>
                </div>
                <div className="p-4 bg-background">
                  <pre className="text-xs overflow-x-auto">
                    <code>{file.content}</code>
                  </pre>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  )
}
